import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useCirculars = () => {
  const navigate = useNavigate();
  const [circulars, setCirculars] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCirculars = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.termID) queryObj.termID = params.termID;
        if (params.audience) queryObj.audience = params.audience;
        if (params.school_class) queryObj.school_class = params.school_class;
        if (params.include_inactive) queryObj.include_inactive = "true";

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.circulars}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setCirculars(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch circulars");
        }
      } catch (error) {
        message.error("Error fetching circulars");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchCircularDetail = useCallback(
    async (id) => {
      try {
        const url = `${domainSettings.urls.academics.circularDetail.replace(":id", id)}?include_inactive=true`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data;
        Utilities.showError(response, "Failed to fetch circular details");
        return null;
      } catch (error) {
        message.error("Error fetching circular details");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const createCircular = useCallback(
    async (formData) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.circulars,
          formData,
          navigate,
        );
        if (response.ok) {
          message.success("Circular created successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to create circular");
        return null;
      } catch (error) {
        message.error("Error creating circular");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updateCircular = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.circularDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Circular updated successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to update circular");
        return null;
      } catch (error) {
        message.error("Error updating circular");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const activateCircular = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.circularActivate.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Circular activated successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to activate circular");
        return false;
      } catch (error) {
        message.error("Error activating circular");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const deactivateCircular = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.circularDeactivate.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Circular deactivated successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to deactivate circular");
        return false;
      } catch (error) {
        message.error("Error deactivating circular");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const archiveCircular = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.circularArchive.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Circular archived successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to archive circular");
        return false;
      } catch (error) {
        message.error("Error archiving circular");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const hardDeleteCircular = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.circularDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Circular deleted successfully");
          return true;
        }
        Utilities.showError(response, "Failed to delete circular");
        return false;
      } catch (error) {
        message.error("Error deleting circular");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const uploadCircularFiles = useCallback(
    async (id, files) => {
      try {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const url = domainSettings.urls.academics.circularFiles.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          formData,
          navigate,
        );
        if (response.ok) {
          message.success("Files uploaded successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to upload files");
        return null;
      } catch (error) {
        message.error("Error uploading files");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const deleteCircularFile = useCallback(
    async (circularId, fileId) => {
      try {
        const url = domainSettings.urls.academics.circularFileDelete
          .replace(":id", circularId)
          .replace(":fileId", fileId);
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("File removed successfully");
          return true;
        }
        Utilities.showError(response, "Failed to remove file");
        return false;
      } catch (error) {
        message.error("Error removing file");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  return {
    circulars,
    loading,
    fetchCirculars,
    fetchCircularDetail,
    createCircular,
    updateCircular,
    activateCircular,
    deactivateCircular,
    archiveCircular,
    hardDeleteCircular,
    uploadCircularFiles,
    deleteCircularFile,
  };
};

export default useCirculars;
